import isEmpty from "lodash/isEmpty";
import { FC, useEffect, useMemo, useState } from "react";
import { useGetLoggedInUserDetails } from "../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
} from "../../../../../../../../components";
import { WAVE_COHORT_SITE_CORE_KEY } from "../../../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../../../helpers/constants/countryDateFormat";
import { getFieldRuleByCountry } from "../../../../../../../../helpers/constants/countryFieldRulesUtils";
import {
  calculateAge,
  convertToUserFormat,
  formatFullNameWithPrefix,
  getValueOrBlank,
} from "../../../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../../../hooks/useGridContainer";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import {
  ParticipantDetail,
  ParticipantTabFormKey,
} from "../../../../../../../createProgram/modelsAndSchemas/Models";
import { programSiteCoreKeys } from "../../../../../../../createProgram/siteCore/SiteCoreKeys";

interface ParticipantDetailsProp {
  participantDetail?: ParticipantDetail;
  preferredLanguageName?: string;
  deliveryCountry?: string;
  isTMAssessmentProgram?: boolean;
  isTMLeaderCoachingProgram?: boolean;
}

export const ParticipantDetails: FC<ParticipantDetailsProp> = ({
  participantDetail,
  preferredLanguageName,
  deliveryCountry = "",
  isTMAssessmentProgram,
  isTMLeaderCoachingProgram,
}) => {
  const {
    fetchLabel,
    fetchValueByKeyOfDropdown,
    commonLabels,
    fetchDropdownListKeyValueForCountrySpecific,
  } = useSiteCore();
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();
  const [showWaveCohortField, setShowWaveCohortField] =
    useState<boolean>(false);

  const getLabel = (parent: string, key: string) => {
    return fetchLabel(parent, key);
  };

  useEffect(() => {
    setShowWaveCohortField(
      participantDetail?.batchTypeKey === WAVE_COHORT_SITE_CORE_KEY,
    );
  }, [participantDetail?.batchTypeKey]);

  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const calculateAgeIfPossible = (dateOfBirth: string): string => {
    return dateOfBirth && userCountryDateFormat
      ? calculateAge(dateOfBirth, userCountryDateFormat).toString()
      : "";
  };

  const formatDate = (date: string | undefined): string => {
    return date ? convertToUserFormat(date, userCountryDateFormat) : "";
  };

  const {
    participantInformation,
    participantName,
    dateOfBirth,
    gender,
    personalId,
    age,
    telephone,
    mobile,
    appendPhone,
    email,
    workEMail,
    spousePartner,
    educationLevel,
    mustStartByDate,
    actualStartDate,
    projectedProgramEndDate,
    primaryLanguage,
    textSubscribedDate,
    textUnsubscribedDate,
    emailUnsubscribedDate,
    prefixList,
    genderList,
    nisId,
    languageList,
    tPSBlacklistStatus,
    ageRange,
    preferredName,
    preferredLanguage,
    batchType,
    waveCohortDetails,
    batchTypeList,
  } = programSiteCoreKeys;

  const genderRule = getFieldRuleByCountry(
    deliveryCountry,
    ParticipantTabFormKey.gender,
  );

  const dateOfBirthRule = getFieldRuleByCountry(
    deliveryCountry,
    ParticipantTabFormKey.dateOfBirth,
  );

  const personalIdRule = getFieldRuleByCountry(
    deliveryCountry,
    ParticipantTabFormKey.personalId,
  );

  const countrySpecificParticipantDetail = useMemo(() => {
    const educationLevelValue = fetchDropdownListKeyValueForCountrySpecific(
      programSiteCoreKeys.participantInformation,
      programSiteCoreKeys.educationLevelListPrefix,
      deliveryCountry,
      getValueOrBlank(participantDetail?.educationLevelKey),
    );

    return { educationLevelValue };
  }, [deliveryCountry, participantDetail]);

  return (
    <MuiBox className="content">
      <MuiGrid container data-testid="participate-details">
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(participantInformation, participantName)}
            replaceEmptyWithDash
            isValueWithEllipsis
            value={formatFullNameWithPrefix(
              getValueOrBlank(participantDetail?.lastName),
              getValueOrBlank(participantDetail?.firstName),
              fetchValueByKeyOfDropdown(
                participantInformation,
                prefixList,
                getValueOrBlank(participantDetail?.prefixKey),
              ),
            )}
          />
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(participantInformation, preferredName)}
            replaceEmptyWithDash
            isValueWithEllipsis
            value={participantDetail?.preferredName}
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(participantInformation, ageRange)}
            replaceEmptyWithDash
            isValueWithEllipsis
            value={fetchValueByKeyOfDropdown(
              participantInformation,
              programSiteCoreKeys.ageRangeList,
              getValueOrBlank(participantDetail?.ageRange),
            )}
          />
        </MuiGrid>

        {dateOfBirthRule.visible && (
          <>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDoubleLabel
                label={getLabel(participantInformation, dateOfBirth)}
                replaceEmptyWithDash
                value={
                  isEmpty(participantDetail?.dateOfBirth)
                    ? ""
                    : formatDate(participantDetail?.dateOfBirth)
                }
              />
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDoubleLabel
                label={getLabel(participantInformation, age)}
                replaceEmptyWithDash
                isValueWithEllipsis
                value={calculateAgeIfPossible(
                  formatDate(participantDetail?.dateOfBirth as string),
                )}
              />
            </MuiGrid>
          </>
        )}

        {genderRule.visible && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(participantInformation, gender)}
              replaceEmptyWithDash
              isValueWithEllipsis
              value={fetchValueByKeyOfDropdown(
                participantInformation,
                genderList,
                getValueOrBlank(participantDetail?.genderKey),
              )}
            />
          </MuiGrid>
        )}

        {personalIdRule.visible && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(participantInformation, personalId)}
              replaceEmptyWithDash
              isValueWithEllipsis
              value={getValueOrBlank(participantDetail?.personalId)}
            />
          </MuiGrid>
        )}

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(participantInformation, telephone)}
            replaceEmptyWithDash
            isValueWithEllipsis
            value={`${getValueOrBlank(participantDetail?.telephoneCountryCode)} ${getValueOrBlank(participantDetail?.telephone)}`.trim()}
          />
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(participantInformation, mobile)}
            replaceEmptyWithDash
            isValueWithEllipsis
            value={`${getValueOrBlank(participantDetail?.mobileCountryCode)} ${getValueOrBlank(participantDetail?.mobile)}`.trim()}
          />
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(participantInformation, appendPhone)}
            replaceEmptyWithDash
            isValueWithEllipsis
            value={`${getValueOrBlank(participantDetail?.appendPhoneCountryCode)} ${getValueOrBlank(participantDetail?.appendPhone)}`.trim()}
          />
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(participantInformation, email)}
            replaceEmptyWithDash
            isValueWithEllipsis
            value={participantDetail?.email}
          />
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(participantInformation, workEMail)}
            replaceEmptyWithDash
            isValueWithEllipsis
            value={participantDetail?.workEmail}
          />
        </MuiGrid>

        {!isTMAssessmentProgram && !isTMLeaderCoachingProgram && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(participantInformation, spousePartner)}
              replaceEmptyWithDash
              isValueWithEllipsis
              value={participantDetail?.spouse}
            />
          </MuiGrid>
        )}

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(participantInformation, educationLevel)}
            replaceEmptyWithDash
            isValueWithEllipsis
            value={countrySpecificParticipantDetail.educationLevelValue}
          />
        </MuiGrid>

        {!isTMAssessmentProgram && !isTMLeaderCoachingProgram && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(participantInformation, nisId)}
              replaceEmptyWithDash
              isValueWithEllipsis
              value={participantDetail?.nisid}
            />
          </MuiGrid>
        )}

        {!isTMAssessmentProgram && !isTMLeaderCoachingProgram && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(participantInformation, mustStartByDate)}
              replaceEmptyWithDash
              value={
                isEmpty(participantDetail?.mustStartByDate)
                  ? ""
                  : formatDate(participantDetail?.mustStartByDate)
              }
            />
          </MuiGrid>
        )}

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(participantInformation, actualStartDate)}
            replaceEmptyWithDash
            value={
              isEmpty(participantDetail?.actualStartDate)
                ? ""
                : formatDate(participantDetail?.actualStartDate)
            }
          />
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(participantInformation, projectedProgramEndDate)}
            replaceEmptyWithDash
            value={
              isEmpty(participantDetail?.projectedProgramEndDate)
                ? ""
                : formatDate(participantDetail?.projectedProgramEndDate)
            }
          />
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(participantInformation, primaryLanguage)}
            replaceEmptyWithDash
            isValueWithEllipsis
            value={fetchValueByKeyOfDropdown(
              participantInformation,
              languageList,
              getValueOrBlank(participantDetail?.primaryLanguageKey),
            )}
          />
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(participantInformation, preferredLanguage)}
            replaceEmptyWithDash
            isValueWithEllipsis
            value={preferredLanguageName}
          />
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(participantInformation, textSubscribedDate)}
            replaceEmptyWithDash
            value={
              isEmpty(participantDetail?.textSubscribedDate)
                ? ""
                : formatDate(participantDetail?.textSubscribedDate)
            }
          />
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(participantInformation, textUnsubscribedDate)}
            replaceEmptyWithDash
            value={
              isEmpty(participantDetail?.textUnsubscribeDate)
                ? ""
                : formatDate(participantDetail?.textUnsubscribeDate)
            }
          />
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(participantInformation, emailUnsubscribedDate)}
            replaceEmptyWithDash
            value={
              isEmpty(participantDetail?.emailUnsubscribeDate)
                ? ""
                : formatDate(participantDetail?.emailUnsubscribeDate)
            }
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(participantInformation, tPSBlacklistStatus)}
            replaceEmptyWithDash
            isValueWithEllipsis
            value={
              participantDetail?.isTPSBlacklist
                ? commonLabels.yes
                : commonLabels.no
            }
          />
        </MuiGrid>

        {isTMAssessmentProgram && (
          <>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDoubleLabel
                label={getLabel(participantInformation, batchType)}
                replaceEmptyWithDash
                isValueWithEllipsis
                value={fetchValueByKeyOfDropdown(
                  participantInformation,
                  batchTypeList,
                  getValueOrBlank(participantDetail?.batchTypeKey),
                )}
              />
            </MuiGrid>
            {showWaveCohortField && (
              <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                <MuiDoubleLabel
                  label={getLabel(participantInformation, waveCohortDetails)}
                  replaceEmptyWithDash
                  isValueWithEllipsis
                  value={getValueOrBlank(participantDetail?.waveCohort)}
                />
              </MuiGrid>
            )}
          </>
        )}
      </MuiGrid>
    </MuiBox>
  );
};
